// AppLayout.tsx
import { FloatingChatButton } from "@/components/chat/FloatingChatButton";
import { PageProvider } from "@/components/page/PageContext";
import { ReportForceBar } from "@/components/page/ReportForceBar";
import { RetailHeaderBar } from "@/components/page/RetailHeaderBar";
import { RetailAppSidebar } from "@/components/sidebar/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/toaster";
import { useAirport } from "@/stores/AirportContext";
import { useReportContext } from "@/stores/ReportContext";
import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";

interface AppLayoutProps {
  enableForceBar?: boolean;
}

const NoRetailReportState: React.FC = () => {
  const { isLoading } = useReportContext();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="mx-4 mt-6 rounded-lg border border-gray-700 bg-gray-900 p-6 text-gray-100">
      <h2 className="text-2xl font-semibold text-white">
        No report available yet
      </h2>
      <p className="mt-2 text-sm text-gray-300">
        This airport does not have a ready live report yet. You can switch to a
        different airport from the header, or wait for the next forecast run to
        complete.
      </p>
    </div>
  );
};

const RetailReportContentGate: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { hasReport } = useReportContext();

  if (!hasReport) {
    return <NoRetailReportState />;
  }

  return <>{children}</>;
};

const RetailLayout: React.FC<AppLayoutProps> = ({ enableForceBar = true }) => {
  const { retailAiChatEnabled } = useAirport();
  const location = useLocation();
  const uiContext = {
    current_page: location.pathname,
  };

  // Sync active retail context to sessionStorage so the /chat (Intelligence) page
  // can pick it up when navigating without losing the selected store / page context.
  // Key: "ai.lastContext" — read by ChatPage on mount and by the Overview nav link.
  // IMPORTANT: Only write for retail overview paths. We must NOT overwrite the context
  // when the user navigates to /chat, /flights, /forecast, /plays etc. — those pages
  // are also wrapped by RetailLayout, and overwriting would erase the saved store path
  // that we want to restore when the user clicks Overview.
  const isRetailOverviewPath =
    location.pathname === "/" ||
    location.pathname.startsWith("/store/") ||
    location.pathname.startsWith("/market-demand");

  useEffect(() => {
    if (isRetailOverviewPath) {
      sessionStorage.setItem("ai.lastContext", JSON.stringify(uiContext));
    }
  }, [location.pathname]);

  return (
    <SidebarProvider>
      <RetailAppSidebar />
      <main className="flex-1 overflow-y-auto min-w-0">
        <PageProvider>
          <RetailHeaderBar />
          <ReportForceBar />
          <div className="p-2 md:p-4">
            <RetailReportContentGate>
              <Outlet />
            </RetailReportContentGate>
          </div>
        </PageProvider>
      </main>
      {retailAiChatEnabled && <FloatingChatButton uiContext={uiContext} />}
      <Toaster />
    </SidebarProvider>
  );
};

export default RetailLayout;
